/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import models.LocalAccount
import models.LocalPlaidToken
import models.Transaction

/**
 * Result wrapper for database operations
 */
sealed class DatabaseResult<out T> {
    data class Success<T>(
        val data: T,
    ) : DatabaseResult<T>()

    data class Error(
        val exception: Throwable,
        val message: String? = null,
    ) : DatabaseResult<Nothing>()

    inline fun <R> map(transform: (T) -> R): DatabaseResult<R> =
        when (this) {
            is Success -> Success(transform(data))
            is Error -> this
        }

    inline fun onSuccess(action: (T) -> Unit): DatabaseResult<T> {
        if (this is Success) action(data)
        return this
    }

    inline fun onError(action: (Throwable, String?) -> Unit): DatabaseResult<T> {
        if (this is Error) action(exception, message)
        return this
    }
}

/**
 * Data class for paginated results
 */
data class PagedResult<T>(
    val items: List<T>,
    val totalCount: Long,
    val hasMore: Boolean,
    val offset: Long,
    val limit: Long,
)

/**
 * Transaction filter criteria for flexible querying
 */
data class TransactionFilter(
    val userId: String,
    val accountIds: List<String>? = null,
    val startDate: String? = null, // YYYY-MM-DD format
    val endDate: String? = null, // YYYY-MM-DD format
    val categories: List<String>? = null,
    val isPending: Boolean? = null,
    val minAmount: Double? = null,
    val maxAmount: Double? = null,
    val searchQuery: String? = null, // Search in name/merchant
    val excludeHidden: Boolean = true,
)

/**
 * Sort options for transaction queries
 */
enum class TransactionSortOrder {
    DATE_DESC, // Most recent first (default)
    DATE_ASC, // Oldest first
    AMOUNT_DESC, // Highest amount first
    AMOUNT_ASC, // Lowest amount first
    MERCHANT_ASC, // Alphabetical by merchant
}

/**
 * Account filter criteria
 */
data class AccountFilter(
    val userId: String,
    val types: List<String>? = null, // checking, savings, credit, etc.
    val includeInactive: Boolean = false,
)

/**
 * Database repository interface for cross-platform data access
 * This interface defines all database operations needed by the application
 */
interface DatabaseRepository {
    // ========================================================================
    // TRANSACTION OPERATIONS
    // ========================================================================

    /**
     * Insert a new transaction into the database
     */
    suspend fun insertTransaction(transaction: Transaction): DatabaseResult<Unit>

    /**
     * Insert multiple transactions (batch operation)
     */
    suspend fun insertTransactions(transactions: List<Transaction>): DatabaseResult<Unit>

    /**
     * Get transaction by ID
     */
    suspend fun getTransactionById(transactionId: String): DatabaseResult<Transaction?>

    /**
     * Get transactions for a user with filtering and pagination
     */
    suspend fun getTransactions(
        filter: TransactionFilter,
        sortOrder: TransactionSortOrder = TransactionSortOrder.DATE_DESC,
        limit: Long = 50,
        offset: Long = 0,
    ): DatabaseResult<PagedResult<Transaction>>

    /**
     * Get transactions for a specific account
     */
    suspend fun getTransactionsByAccount(
        accountId: String,
        limit: Long = 50,
        offset: Long = 0,
    ): DatabaseResult<List<Transaction>>

    /**
     * Get recent transactions (last 30 days)
     */
    suspend fun getRecentTransactions(
        userId: String,
        limit: Long = 20,
    ): DatabaseResult<List<Transaction>>

    /**
     * Get pending transactions for a user
     */
    suspend fun getPendingTransactions(userId: String): DatabaseResult<List<Transaction>>

    /**
     * Search transactions by merchant name or description
     */
    suspend fun searchTransactions(
        userId: String,
        query: String,
        limit: Long = 50,
    ): DatabaseResult<List<Transaction>>

    /**
     * Update transaction (typically for sync from Plaid API)
     */
    suspend fun updateTransaction(transaction: Transaction): DatabaseResult<Unit>

    /**
     * Update transaction user customizations (notes, category, tags, etc.)
     */
    suspend fun updateTransactionUserData(
        transactionId: String,
        userCategory: String? = null,
        userNotes: String? = null,
        tags: List<String>? = null,
        isHidden: Boolean? = null,
        isExpenseReport: Boolean? = null,
    ): DatabaseResult<Unit>

    /**
     * Delete transaction
     */
    suspend fun deleteTransaction(transactionId: String): DatabaseResult<Unit>

    /**
     * Delete transactions for an account (when account is removed)
     */
    suspend fun deleteTransactionsByAccount(accountId: String): DatabaseResult<Unit>

    /**
     * Get transaction count for a user
     */
    suspend fun getTransactionCount(userId: String): DatabaseResult<Long>

    // ========================================================================
    // ACCOUNT OPERATIONS
    // ========================================================================

    /**
     * Insert a new account
     */
    suspend fun insertAccount(account: LocalAccount): DatabaseResult<Unit>

    /**
     * Insert multiple accounts (batch operation)
     */
    suspend fun insertAccounts(accounts: List<LocalAccount>): DatabaseResult<Unit>

    /**
     * Get account by ID
     */
    suspend fun getAccountById(accountId: String): DatabaseResult<LocalAccount?>

    /**
     * Get all accounts for a user
     */
    suspend fun getAccountsByUser(filter: AccountFilter): DatabaseResult<List<LocalAccount>>

    /**
     * Get accounts by item (Plaid item)
     */
    suspend fun getAccountsByItem(itemId: String): DatabaseResult<List<LocalAccount>>

    /**
     * Update account balance (from Plaid sync)
     */
    suspend fun updateAccountBalance(
        accountId: String,
        availableBalance: Double?,
        currentBalance: Double?,
        creditLimit: Double?,
        lastUpdated: String,
    ): DatabaseResult<Unit>

    /**
     * Update account information
     */
    suspend fun updateAccount(account: LocalAccount): DatabaseResult<Unit>

    /**
     * Delete account
     */
    suspend fun deleteAccount(accountId: String): DatabaseResult<Unit>

    /**
     * Delete accounts by item (when Plaid item is removed)
     */
    suspend fun deleteAccountsByItem(itemId: String): DatabaseResult<Unit>

    // ========================================================================
    // PLAID TOKEN OPERATIONS
    // ========================================================================

    /**
     * Insert Plaid token
     */
    suspend fun insertPlaidToken(token: LocalPlaidToken): DatabaseResult<Unit>

    /**
     * Get token by item ID
     */
    suspend fun getPlaidTokenByItemId(itemId: String): DatabaseResult<LocalPlaidToken?>

    /**
     * Get all tokens for a user
     */
    suspend fun getPlaidTokensByUser(userId: String): DatabaseResult<List<LocalPlaidToken>>

    /**
     * Get active tokens for a user (tokens that can make API calls)
     */
    suspend fun getActivePlaidTokens(userId: String): DatabaseResult<List<LocalPlaidToken>>

    /**
     * Update token state (for lifecycle management)
     */
    suspend fun updatePlaidTokenState(
        itemId: String,
        state: String,
        errorCode: String? = null,
        errorMessage: String? = null,
    ): DatabaseResult<Unit>

    /**
     * Mark token as used (update last_used_at)
     */
    suspend fun markPlaidTokenUsed(itemId: String): DatabaseResult<Unit>

    /**
     * Update token access information
     */
    suspend fun updatePlaidToken(token: LocalPlaidToken): DatabaseResult<Unit>

    /**
     * Delete Plaid token
     */
    suspend fun deletePlaidToken(itemId: String): DatabaseResult<Unit>

    // ========================================================================
    // USER OPERATIONS
    // ========================================================================

    /**
     * Create or update user
     */
    suspend fun upsertUser(
        userId: String,
        username: String,
        email: String? = null,
    ): DatabaseResult<Unit>

    /**
     * Update user last login timestamp
     */
    suspend fun updateUserLastLogin(userId: String): DatabaseResult<Unit>

    /**
     * Get user information
     */
    suspend fun getUser(userId: String): DatabaseResult<Map<String, Any?>?>

    /**
     * Delete user and all associated data
     */
    suspend fun deleteUser(userId: String): DatabaseResult<Unit>

    // ========================================================================
    // UTILITY OPERATIONS
    // ========================================================================

    /**
     * Execute database transaction (atomic operations)
     */
    suspend fun <T> executeTransaction(block: suspend () -> T): DatabaseResult<T>

    /**
     * Clear all data for a user (for account deletion/reset)
     */
    suspend fun clearUserData(userId: String): DatabaseResult<Unit>

    /**
     * Get database health/statistics
     */
    suspend fun getDatabaseStats(): DatabaseResult<Map<String, Any>>

    /**
     * Optimize database (vacuum, reindex, etc.)
     */
    suspend fun optimizeDatabase(): DatabaseResult<Unit>

    // ========================================================================
    // SYNC OPERATIONS
    // ========================================================================

    /**
     * Get last sync timestamp for an item
     */
    suspend fun getLastSyncTime(itemId: String): DatabaseResult<String?>

    /**
     * Update last sync timestamp for an item
     */
    suspend fun updateLastSyncTime(
        itemId: String,
        timestamp: String,
    ): DatabaseResult<Unit>

    /**
     * Get transactions that need to be synced (modified locally)
     */
    suspend fun getTransactionsPendingSync(userId: String): DatabaseResult<List<Transaction>>

    /**
     * Mark transactions as synced
     */
    suspend fun markTransactionsAsSynced(transactionIds: List<String>): DatabaseResult<Unit>

    // TODO: Authentication operations will be added later when auth service is restored
}

/**
 * Extension functions for convenience
 */
suspend fun DatabaseRepository.getTransactionsByUserSimple(
    userId: String,
    limit: Long = 50,
): DatabaseResult<List<Transaction>> =
    getTransactions(
        filter = TransactionFilter(userId = userId),
        limit = limit,
    ).map { it.items }

suspend fun DatabaseRepository.getAccountsByUserSimple(userId: String): DatabaseResult<List<LocalAccount>> =
    getAccountsByUser(AccountFilter(userId = userId))

/**
 * Result handling extensions
 */
inline fun <T> DatabaseResult<T>.fold(
    onSuccess: (T) -> Unit,
    onError: (Throwable, String?) -> Unit,
) {
    when (this) {
        is DatabaseResult.Success -> onSuccess(data)
        is DatabaseResult.Error -> onError(exception, message)
    }
}

inline fun <T> DatabaseResult<T>.getOrNull(): T? =
    when (this) {
        is DatabaseResult.Success -> data
        is DatabaseResult.Error -> null
    }

inline fun <T> DatabaseResult<T>.getOrThrow(): T =
    when (this) {
        is DatabaseResult.Success -> data
        is DatabaseResult.Error -> throw exception
    }
